<template>
  <div data-test-id="message-image-grid" :class="['message-image-grid', `message-image-grid--count-${gridClassCount}`]">
    <div
      v-for="(image, index) in visibleImages"
      :key="image.id"
      class="message-image-grid__cell"
      @click="$emit('open', index)"
    >
      <img
        data-test-id="message-image"
        class="message-image-grid__img"
        :src="image.signedUrl"
        :alt="image.name"
        loading="lazy"
      />
      <div
        v-if="index === 3 && hasMore"
        data-test-id="message-image-overlay-more"
        class="message-image-grid__overlay-more"
      >
        +{{ hiddenCount }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type ImageItemType = { id: string; name: string; ext: string; signedUrl?: string }

const props = defineProps<{
  images: ImageItemType[]
}>()

defineEmits<{
  (e: 'open', index: number): void
}>()

const MAX_VISIBLE = 4

const hasMore = computed(() => props.images.length > MAX_VISIBLE)
const hiddenCount = computed(() => Math.max(0, props.images.length - MAX_VISIBLE))
const visibleImages = computed(() => props.images.slice(0, MAX_VISIBLE))
const gridClassCount = computed(() => {
  const n = props.images.length
  if (n >= 5) return '5plus'
  return String(n)
})
</script>

<style lang="scss">
.message-image-grid {
  display: grid;
  gap: 4px;
  max-width: 300px;
  border-radius: 8px;
  overflow: hidden;

  &__cell {
    position: relative;
    cursor: pointer;
    overflow: hidden;
    background: #eee;

    &:hover .message-image-grid__img {
      filter: brightness(0.95);
    }
  }

  &__img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__overlay-more {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    pointer-events: none;
  }

  &--count-1 {
    grid-template-columns: 1fr;

    .message-image-grid__cell {
      max-height: 300px;

      .message-image-grid__img {
        height: auto;
        max-height: 300px;
      }
    }
  }

  &--count-2 {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 150px;
  }

  &--count-3 {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-auto-rows: 90px;

    .message-image-grid__cell:first-child {
      grid-row: span 2;
    }
  }

  &--count-4,
  &--count-5plus {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 140px;
  }
}
</style>
